import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLiveUpdate } from "@/contexts/LiveUpdateContext";
import { useTheme } from "@/contexts/ThemeContext";
import {
  LayoutDashboard, ListTodo, Columns3, Activity, LogOut,
  ChevronLeft, ChevronRight, Menu, User, Users, Bell,
  Sun, Moon, Map, CalendarDays
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

const navItems = [
  { to: "/", icon: LayoutDashboard, label: "Dasbor" },
  { to: "/tasks", icon: ListTodo, label: "Tugas" },
  { to: "/kanban", icon: Columns3, label: "Papan Kanban" },
  { to: "/routine-matrix", icon: CalendarDays, label: "Tugas Rutin" },
  { to: "/activity", icon: Activity, label: "Log Aktivitas" },
  { to: "/roadmap", icon: Map, label: "Roadmap" },
  { to: "/users", icon: Users, label: "Pengguna", managerOnly: true },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { notifications, unreadCount, clearNotifications } = useLiveUpdate();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="flex items-center justify-center px-4 h-16 border-b border-slate-800">
        {collapsed ? (
          <div className="w-9 h-9 rounded-lg bg-[#00A9EA] flex items-center justify-center shrink-0">
            <span className="text-white font-black text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>FT</span>
          </div>
        ) : (
          <img src="/logo/FlowTik-dark.svg" alt="FlowTik" className="h-7 w-auto" />
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 px-3 space-y-1" data-testid="sidebar-nav">
        {navItems
          .filter((item) => !item.managerOnly || ["superadmin", "kepala_tik", "kapokja"].includes(user?.role))
          .map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200 ${
                isActive
                  ? "bg-blue-600/15 text-blue-400 border-r-2 border-blue-500"
                  : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"
              } ${collapsed ? "justify-center" : ""}`
            }
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <item.icon className="w-5 h-5 shrink-0" />
            {!collapsed && <span>{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* User info */}
      {!collapsed && user && (
        <div className="px-4 py-3 border-t border-slate-800 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center overflow-hidden shrink-0">
            {user.picture ? (
              <img src={user.picture.startsWith('http') ? user.picture : `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}${user.picture}`} alt={user.name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-white text-sm font-bold">
                {(user.name || "User").split(" ").map(n => n && n[0]).join("").toUpperCase().slice(0, 2)}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">Masuk sebagai</div>
              <div className="text-sm text-slate-300 font-medium truncate">{user.name}</div>
              <div className="text-xs text-slate-500 capitalize">{
                user.role === "superadmin" ? "Superadmin" : 
                user.role === "ka_div" ? "Ka Div" : 
                user.role === "kepala_tik" ? "Kepala TIK" : 
                user.role === "kapokja" ? "Kapokja" : "Staf"
              }</div>
            </div>
        </div>
      )}
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-slate-950">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex flex-col bg-slate-950 transition-all duration-300 shrink-0 ${
          collapsed ? "w-[68px]" : "w-64"
        }`}
        data-testid="sidebar"
      >
        <SidebarContent />
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center justify-center h-10 border-t border-slate-800 text-slate-500 hover:text-slate-300 transition-colors"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-slate-950 flex flex-col z-50">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header
          className="sticky top-0 z-40 flex items-center justify-between h-16 px-4 md:px-8 bg-white/70 dark:bg-slate-900/80 backdrop-blur-xl backdrop-saturate-150 border-b border-slate-200 dark:border-slate-800"
          data-testid="header"
        >
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden p-2 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
              data-testid="mobile-menu-button"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center gap-3 relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="w-10 h-10 rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
              title="Ganti Mode Gelap"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>

            {/* Notification Bell */}
            <div className="relative">
              <button 
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  if (showNotifications && unreadCount > 0) clearNotifications();
                }}
                className="relative p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                data-testid="notification-bell"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white ring-2 ring-white">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notification Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-slate-200 overflow-hidden z-50 origin-top-right animate-in fade-in slide-in-from-top-2">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                    <h3 className="font-semibold text-slate-800 text-sm">Notifikasi</h3>
                    {notifications.length > 0 && (
                      <button 
                        onClick={clearNotifications}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Tandai semua dibaca
                      </button>
                    )}
                  </div>
                  <div className="max-h-[300px] overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-sm text-slate-500">
                        Tidak ada notifikasi baru
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {notifications.map((notif, idx) => (
                          <div 
                            key={idx} 
                            className="px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer"
                            onClick={() => {
                              setShowNotifications(false);
                              if (notif.task_id) navigate(`/tasks`); // Optional: add logic to open specific task
                            }}
                          >
                            <p className="text-sm text-slate-800" dangerouslySetInnerHTML={{__html: notif.message}} />
                            <span className="text-[10px] text-slate-400 mt-1 block">Baru saja</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-slate-100"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center overflow-hidden shrink-0">
                      {user.picture ? (
                        <img src={user.picture.startsWith('http') ? user.picture : `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}${user.picture}`} alt={user.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-white text-xs font-bold">
                          {user.name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)}
                        </span>
                      )}
                    </div>
                    <span className="hidden sm:block text-sm text-slate-700 font-medium">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuLabel>
                    <div className="text-sm font-medium">{user?.name}</div>
                    <div className="text-xs text-slate-500">{user?.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    className="text-sm cursor-pointer"
                    onClick={() => navigate("/profile")}
                  >
                    <User className="w-4 h-4 mr-2" />
                    Profil
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-xs text-slate-500 capitalize">
                    <Users className="w-4 h-4 mr-2" />
                    Peran: {
                      user.role === "superadmin" ? "Superadmin" : 
                      user.role === "ka_div" ? "Ka Div" : 
                      user.role === "kepala_tik" ? "Kepala TIK" : 
                      user.role === "kapokja" ? "Kapokja" : "Staf"
                    }
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="text-red-600 focus:text-red-600"
                    data-testid="logout-button"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Keluar
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="page-enter">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
